'use client';

import {
  Toaster as ChakraToaster,
  Portal,
  Spinner,
  Stack,
  Toast,
  createToaster,
} from '@chakra-ui/react';
import { Check, InfoCircle, Sparks, WarningTriangle, Xmark, XmarkCircle } from 'iconoir-react';
import { CloseButton } from './close-button';

const DEFAULT_TOAST_DURATION_MS = 5000;

export const toaster = createToaster({
  placement: 'bottom-end',
  pauseOnPageIdle: true,
  duration: DEFAULT_TOAST_DURATION_MS,
  max: 3,
  gap: 10,
  offsets: '26px',
  // Must cover the 280ms toast-slide-out exit animation, else the node
  // unmounts mid-flight (zag default is 200ms).
  removeDelay: 280,
});

const toastVisuals = {
  success: Check,
  error: XmarkCircle,
  warning: WarningTriangle,
  info: InfoCircle,
  default: Sparks,
} as const;

const getToastIcon = (type?: string) => {
  if (type === 'success') return toastVisuals.success;
  if (type === 'error') return toastVisuals.error;
  if (type === 'warning') return toastVisuals.warning;
  if (type === 'info') return toastVisuals.info;
  return toastVisuals.default;
};

// 2 * PI * r for the r=13 circles below; the toast-ring keyframe in
// packages/theme empties this exact length over the toast duration.
const RING_CIRCUMFERENCE = 81.7;

const ToastCountdownRing = ({ durationMs }: { durationMs?: number }) => {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" data-toast-ring>
      <circle cx="15" cy="15" r="13" fill="none" strokeWidth="2" data-toast-ring-track />
      {durationMs !== undefined && (
        <circle
          cx="15"
          cy="15"
          r="13"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          data-toast-ring-fill
          style={{ animationDuration: `${durationMs}ms` }}
        />
      )}
    </svg>
  );
};

export const Toaster = () => {
  return (
    <Portal>
      <ChakraToaster toaster={toaster} insetInline={{ mdDown: '4' }}>
        {(toast) => {
          const Icon = getToastIcon(toast.type);
          const durationMs =
            typeof toast.duration === 'number'
              ? Number.isFinite(toast.duration)
                ? toast.duration
                : undefined
              : toast.type === 'loading'
                ? undefined
                : DEFAULT_TOAST_DURATION_MS;

          return (
            <Toast.Root>
              {/* Not Toast.Indicator: Chakra discards its children and only
                  renders built-in icons, see the toast recipe. */}
              <span data-toast-indicator>
                {toast.type === 'loading' ? (
                  <Spinner size="xs" color="currentColor" />
                ) : (
                  <>
                    <ToastCountdownRing durationMs={durationMs} />
                    <Icon width={14} height={14} strokeWidth={2} />
                  </>
                )}
              </span>

              <Stack gap="3px" flex="1" minW={0} maxWidth="100%" pt="1px">
                {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
                {toast.description && <Toast.Description>{toast.description}</Toast.Description>}
                {toast.action && <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>}
              </Stack>

              {/* CloseButton props mirror the closeTrigger slot in the toast
                  recipe: its own defaults beat slot css, so the dark-glass
                  values are re-passed here. */}
              {(toast.closable || toast.meta?.closable) && (
                <Toast.CloseTrigger asChild>
                  <CloseButton
                    size="2xs"
                    rounded="md"
                    borderWidth="0"
                    color="rgba(247, 246, 243, 0.60)"
                    _hover={{ bg: 'rgba(255, 255, 255, 0.09)', color: '#F7F6F3' }}
                  >
                    <Xmark width={14} height={14} />
                  </CloseButton>
                </Toast.CloseTrigger>
              )}
            </Toast.Root>
          );
        }}
      </ChakraToaster>
    </Portal>
  );
};
